import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useRef, useState } from 'react';
import { useFlags } from '@/context/feature-flags';
import type { GeneratedObject } from '../zod';

export function useGenerateNotes() {
  const { flags } = useFlags();
  const aiNotesFlag = flags?.['ai-notes'];
  const { authState } = useOktaAuth();
  const [connection, setConnection] = useState<WebSocket | null>(null);
  const [data, setData] = useState<GeneratedObject>();

  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let mounted = true;

    const connect = async () => {
      try {
        // Skip WebSocket connection if user is not authenticated
        if (!authState?.accessToken?.accessToken) {
          return;
        }

        const url = process.env.REACT_APP_WS_API_URL;

        if (!url) {
          throw new Error('No Web Socket API URL found');
        }

        // Least worst way to "smuggle" auth tokens to websockets:
        // https://stackoverflow.com/a/77060459
        ws = new WebSocket(url, [`token.${authState?.accessToken?.accessToken}`]);

        ws.onopen = () => {
          console.log('Connected to websocket');
          setConnection(ws);
        };

        ws.onmessage = (event) => {
          try {
            const eventData = JSON.parse(event.data);

            if (eventData) {
              switch (eventData.type) {
                case 'analyze':
                  setStatus('Analyzing data...');
                  break;
                case 'no-data':
                  setStatus('No notable items to add to notes.');
                  // Clear any existing timeout and set a new one
                  if (statusTimeoutRef.current) {
                    clearTimeout(statusTimeoutRef.current);
                  }
                  statusTimeoutRef.current = setTimeout(() => {
                    if (mounted) {
                      setStatus(null);
                    }
                  }, 5000);
                  break;
                case 'end':
                  setStatus('Finished generating notes.');
                  // Clear any existing timeout and set a new one
                  if (statusTimeoutRef.current) {
                    clearTimeout(statusTimeoutRef.current);
                  }
                  statusTimeoutRef.current = setTimeout(() => {
                    if (mounted) {
                      setStatus(null);
                    }
                  }, 5000);
                  break;
                case 'object':
                  if (mounted) {
                    // Check if content is wrapped in a properties object and unwrap it
                    let normalizedEventData = eventData;
                    if (eventData.content?.properties) {
                      normalizedEventData = {
                        ...eventData,
                        content: eventData.content.properties,
                      };
                    }
                    setData(normalizedEventData);
                    setError(null);
                    setStatus(null);
                  }
                  break;
                default:
                  setStatus(null);
                  break;
              }
            }
          } catch (err) {
            console.error('Error processing message:', err);

            if (mounted && err instanceof Error) {
              setError(err);
              setStatus(`Error generating notes: ${err.message}`);
              console.log('Error generating notes:', err);
              // Clear any existing timeout and set a new one
              if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
              }
              statusTimeoutRef.current = setTimeout(() => {
                if (mounted) {
                  setStatus(null);
                }
              }, 5000);
            }
          }
        };

        ws.onerror = (event) => {
          console.error('WebSocket error:', event);

          if (mounted) {
            setError(new Error('WebSocket connection error'));
          }
        };

        ws.onclose = () => {
          setConnection(null);
          console.log('WebSocket connection closed');
          // Attempt to reconnect after a delay
          if (mounted) {
            setTimeout(connect, 5000);
          }
        };
      } catch (err) {
        if (mounted && err instanceof Error) {
          setError(err);
        }
      }
    };

    connect();

    return () => {
      mounted = false;
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
      if (ws) {
        ws.close();
      }
    };
  }, [authState?.accessToken?.accessToken]);

  // Return null values if user is not authenticated or AI notes flag is disabled
  if (!authState?.accessToken?.accessToken || !aiNotesFlag) {
    return {
      connection: null,
      data: undefined,
      error: null,
      status: null,
      setStatus: () => {},
    };
  }

  return { connection, data, error, status, setStatus };
}
